import * as React from "react";
import {Alert, Button, Grid, Modal} from "react-bootstrap/lib";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ActionBar} from "../../common/ui/actionbar/ActionBar";
import {ErrorDisplay} from "../../common/ui/errordisplay/ErrorDisplay";
import {Spinner} from "../../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../../common/util/ErrorMapper";
import {setStateAsync} from "../../common/util/ReactUtils";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {AOINotificationEvent} from "../model";
import {AOIEventsList} from "./AOIEventsList";
import {DeleteAOIEventButton} from "./DeleteAOIEventButton";

export interface AOIEventsDialogOwnProps {
    onSuccess?: () => void;
    onHide?: () => void;
    notificationId: string;
    notificationAreaId: string;
}

type AOIEventsDialogProps = AOIEventsDialogOwnProps & InjectedIntlProps & WithApiProperties;

interface AOIEventsDialogState {
    isLoading: boolean;
    isExecuting: boolean;
    isSuccess: boolean;
    error: Error;
    allEvents: AOINotificationEvent[];
    selection: AOINotificationEvent[];
}

class AOIEventsDialogComponent extends React.Component<AOIEventsDialogProps, AOIEventsDialogState> {

    constructor(props: AOIEventsDialogProps) {
        super(props);

        this.state = {
            isLoading: false,
            isExecuting: false,
            error: null,
            isSuccess: false,
            allEvents: [],
            selection: [],
        };
    }

    componentDidMount() {
        this.loadData();
    }

    updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

    render() {
        return (
            <div>
                {this.renderModal()}
            </div>
        );
    }

    private loadData() {
        const {api, notificationId, notificationAreaId} = this.props;

        this.updateState({isLoading: true});

        return api.getNotificationEvents(notificationId, notificationAreaId)
            .then((events) => {
                this.updateState({
                    allEvents: events.sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp)),
                    isLoading: false,
                });
            });
    }

    private closeDialog = () => {
        this.props.onHide();
    }

    private renderTitle() {
        const {isExecuting, isSuccess} = this.state;

        if (isExecuting || isSuccess) {
            return null;
        }

        return <Modal.Title><FormattedMessage id="studio.notifications.aoi-events-dialog.title"
                                              defaultMessage="Events"/></Modal.Title>;
    }

    private renderExecuting() {
        const {isExecuting} = this.state;
        if (isExecuting) {
            return (
                <div style={{textAlign: "center"}}>
                    <h2><FormattedMessage id="studio.notifications.aoi-events-dialog.saving"
                                          defaultMessage="Saving..."/></h2>
                    <Spinner bigStyle/>
                </div>
            );
        }
        return null;
    }

    private renderError() {
        const {error} = this.state;

        if (error) {
            const errorMessage = mapErrorToMessage(error) as string;
            if (errorMessage) {
                return <Alert bsStyle="danger">{errorMessage}</Alert>;
            }
            return <ErrorDisplay error={error}/>;
        }
        return null;
    }

    private renderBody() {
        const {isExecuting} = this.state;

        if (isExecuting) {
            return this.renderExecuting();
        }

        const {notificationId, notificationAreaId} = this.props;
        const {allEvents, isLoading, selection} = this.state;

        const noSelection = selection.length === 0;

        return (
            <div>
                {this.renderError()}
                <Grid fluid={true}>
                    <ActionBar>
                        <DeleteAOIEventButton notificationId={notificationId} allEvents={allEvents}
                                              selectedEvents={selection} onDeleteSuccess={this.onDeleteSuccess}
                                              onDeleteWarnings={this.clearSelection} disabled={noSelection}/>
                        <Button onClick={this.markRead} disabled={noSelection}>
                            <FormattedMessage id="studio.notifications.aoi-events-dialog.mark-read"
                                              defaultMessage="Mark as read"/>
                        </Button>
                        <Button onClick={this.markUnread} disabled={noSelection}>
                            <FormattedMessage id="studio.notifications.aoi-events-dialog.mark-unread"
                                              defaultMessage="Mark as unread"/>
                        </Button>
                    </ActionBar>
                    <AOIEventsList notificationId={notificationId} notificationAreaId={notificationAreaId}
                                   data={allEvents} isAllDataLoaded={!isLoading} onRowSelect={this.handleRowSelect}
                                   onRowClick={this.handleRowClick} clearSelection={noSelection}/>
                </Grid>
            </div>
        );
    }

    private onDeleteSuccess = () => {
        this.clearSelection();
        this.loadData();
    };

    private handleRowClick = (event, index) => {
        const {allEvents} = this.state;

        if (event.target.dataset.isHasBeenReadCheckbox) {
            const selectedEvent = allEvents[index];

            setStateAsync(this, {
                isExecuting: true,
                selection: [selectedEvent],
            })
                .then(() => {
                    if (selectedEvent.hasBeenRead) {
                        this.markUnread();
                    } else {
                        this.markRead();
                    }
                });

            return true;
        }
    };

    private markRead = () => {
        this.markEvent(true);
        this.clearSelection();
    };

    private markUnread = () => {
        this.markEvent(false);
        this.clearSelection();
    };

    private markEvent = (hasBeenRead: boolean) => {
        const {api} = this.props;
        const {selection} = this.state;

        this.updateState({isExecuting: true});

        Promise.all(selection.map((event) =>
            api.updateNotificationEvent({...event, hasBeenRead})
        ))
            .then(() => this.loadData())
            .then(() => {
                this.updateState({isExecuting: false});
            });
    };

    private clearSelection = () => {
        this.handleRowSelect([]);
    };

    handleRowSelect = (selection: AOINotificationEvent[]) => {
        this.updateState({selection});
    };

    private renderModal() {
        const {isExecuting} = this.state;
        return (
            <Modal show={true} onHide={this.closeDialog} backdrop="static">
                <Modal.Header closeButton={!isExecuting}>
                    {this.renderTitle()}
                </Modal.Header>
                <Modal.Body>
                    {this.renderBody()}
                </Modal.Body>
            </Modal>
        );
    }
}

export const AOIEventsDialog = WithApi(injectIntl(AOIEventsDialogComponent));